import React, { useCallback } from 'react';
import { withRouter } from 'react-router-dom';
import { compose } from 'redux';
import { connect, useDispatch } from 'react-redux';
import { withProps, withState } from 'recompose';
import { get } from 'lodash';
import { Container } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/styles';

import * as ENUM from '@dpdgroupuk/redback-enums';
import { withPageControl } from '@dpdgroupuk/fmx-ui/components/Page';
import BackToTrackingMapContainer from '@dpdgroupuk/fmx-ui/components/BackToTrackingMap';
import { IMAGES } from '@dpdgroupuk/fmx-ui/themes/constants';
import {
  withTrackProps,
  createLogEntryPayload,
} from '@dpdgroupuk/react-event-tracker';
import { withModal } from '@dpdgroupuk/fmx-ui/components/Dialog';
import Button from '@dpdgroupuk/fmx-ui/components/Button';
import { requestChatDetails } from '@dpdgroupuk/cc-chat-fe';

import type { AuthorizedParcel } from '../../../types';
import {
  WHAT_HAPPENS_NEXT_PAGE_HEADER,
  WHAT_HAPPENS_NEXT_HEADER,
  CHANGE_DELIVERY,
} from '../../../constants/message';
import withParcel from '../../../HOCs/withParcel';
import {
  SPEED_DEAL,
  trackable,
  WHAT_HAPPENS_NEXT,
} from '../../../constants/analytics';
import {
  trackAnotherParcelSpeedDeal,
  goToParcelInfoSpeedDeal,
  trackAnotherParcelHeader,
  backToDpdSiteHeader,
  backToGreenSiteHeader,
  getDpdAppHeader,
  goToParcelInfoHeader,
  goBackToDeliveryDetailsSpeedDeal,
} from '../../controlActions';
import { goToTracking, goToDeliveryOptions } from '../../historyActions';
import { getParcelRoadMapPreferences } from '../model';
import TrackingStatus from '../../../components/TrackingStatus';
import NextSteps from './NextSteps';
import { getDeliveryActionsByDepotCode } from '../../../store/orm/Action/selectors';
import { getClaimsData } from '../../../store/orm/Claim/selectors';
import { fetchActionsNeighboursAndDriver } from '../actions';
import { getBackgroundImage } from '../../../utils/images';
import ChatBotWrapper from './ChatBotWrapper';
import { useChatIconWithAction, useDelay } from '../hooks';
import { ORIGIN } from '../../../constants/values';
import { fetchChatConfig } from '../../../apis/chat';
import withFetch from '../../../HOCs/withFetch';
import NoNexSteps from './NoNextSteps';
import { withRemoteConfig } from '../../../components/RemoteConfig/ConfigContext';
import { CHAT_CONFIG } from '../../../constants/chat';

const useStyles = makeStyles(({ palette, typography, breakpoints }) => ({
  container: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    paddingTop: typography.pxToRem(170),
  },
  title: {
    color: palette.text.primary,
    fontSize: typography.pxToRem(19),
    lineHeight: 1.65,
    letterSpacing: typography.pxToRem(0.13),
    marginTop: typography.pxToRem(29),
    textAlign: 'center',

    [breakpoints.down('xs')]: {
      fontSize: typography.pxToRem(15),
      lineHeight: 1.77,
      letterSpacing: typography.pxToRem(0.37),
    },
  },
  contentWrapper: {
    marginTop: typography.pxToRem(45),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    [breakpoints.down('xs')]: {
      flexDirection: 'column',
      marginTop: typography.pxToRem(23.4),
      alignItems: 'flex-start',
      marginLeft: typography.pxToRem(10),
    },
  },
  stepsWrapper: {
    paddingRight: typography.pxToRem(25),

    [breakpoints.down('xs')]: {
      width: '100%',
      paddingRight: 0,
    },
  },
  button: {
    backgroundColor: palette.error.main,

    '&:hover': {
      backgroundColor: palette.error.main,
    },
  },
  changeWrapper: {
    paddingLeft: typography.pxToRem(56),

    [breakpoints.down('xs')]: {
      paddingLeft: 0,
      marginTop: typography.pxToRem(33),
    },
  },
  divider: {
    width: typography.pxToRem(2),
    height: typography.pxToRem(216),
    backgroundColor: '#979797',

    [breakpoints.down('xs')]: {
      display: 'none',
    },
  },
  width: {
    maxWidth: typography.pxToRem(960),
  },
  status: {
    fontSize: typography.pxToRem(19),
    [breakpoints.down('xs')]: {
      fontSize: typography.pxToRem(16),
    },
    '& .REDTEXT': {
      fontWeight: 500,
      color: 'inherit',
    },
  },
  link: {
    textTransform: 'uppercase',
    marginTop: typography.pxToRem(55),
    color: '#ffffff',
    fontSize: typography.pxToRem(11),
    fontWeight: 'normal',
    fontStretch: 'normal',
    fontStyle: 'normal',
    lineHeight: 'normal',
    letterSpacing: 0.56,
    textAlign: 'center',
    cursor: 'pointer',
    borderRadius: typography.pxToRem(6.5),
    boxShadow: `0 ${typography.pxToRem(2)} ${typography.pxToRem(
      3
    )} 0 rgba(0, 0, 0, 0.5)`,
    backgroundColor: palette.primary.main,
    padding: `${typography.pxToRem(13)} ${typography.pxToRem(19)}`,
    '&:hover': {
      textDecoration: 'none',
    },
    [breakpoints.down('xs')]: {
      padding: `${typography.pxToRem(12)} ${typography.pxToRem(
        17
      )} ${typography.pxToRem(10)}`,
      marginTop: typography.pxToRem(46),
      fontSize: typography.pxToRem(9),
    },
  },
  linkContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: typography.pxToRem(40),
    [breakpoints.down('xs')]: {
      justifyContent: 'flex-start',
      paddingLeft: typography.pxToRem(24),
    },
  },
}));

type Props = {
  parcel: AuthorizedParcel,
  deliveryActions: any,
  goToTracking: Function,
  showChat: boolean,
  loadChat: boolean,
  chatOrigin: boolean,
  setLoadChat: Function,
  setShowChat: Function,
  onChangeDelivery: Function,
  remoteConfig: Config,
};

const WhatHappensNextComponent = ({
  parcel,
  deliveryActions,
  goToTracking,
  showChat,
  loadChat,
  setLoadChat,
  setShowChat,
  chatOrigin,
  onChangeDelivery,
  remoteConfig: { chatbotButtonDelay },
}: Props) => {
  const dispatch = useDispatch();

  // Memoize the delay value to prevent unnecessary re-renders
  const delayValue = React.useMemo(() => Number(chatbotButtonDelay), [
    chatbotButtonDelay,
  ]);
  const delay10s = useDelay(delayValue);

  const availableActions = get(deliveryActions, 'availableActions', []);
  const isKMIinActions = React.useMemo(
    () => availableActions.includes(ENUM.PARCEL_ACTION_CODE.KMI),
    [availableActions]
  );

  // Chat configuration state
  const [chatConfig, setChatConfig] = React.useState(null);
  const [chatConfigLoading, setChatConfigLoading] = React.useState(true);

  // Determine chat availability based on configuration
  // This replicates the logic that getIsChatAvailable would use
  const isChatAvailable = React.useMemo(() => {
    if (!chatConfig) return false;

    // Check if both bot and advisor are available
    const botAvailable = chatConfig.botAvailable === true;
    const advisorAvailable = chatConfig.advisorAvailable === true;

    // Additional checks that might be in the config
    const isWithinServiceWindow = chatConfig.isWithinServiceWindow !== false; // Default true if not specified
    const chatEnabled = chatConfig.chatEnabled !== false; // Default true if not specified

    // Chat is available if:
    // 1. At least one service is available (bot OR advisor)
    // 2. Within service window (if specified)
    // 3. Chat is enabled (if specified)
    const available =
      (botAvailable || advisorAvailable) &&
      isWithinServiceWindow &&
      chatEnabled;

    return available;
  }, [chatConfig]);

  // Memoize query parameters to prevent unnecessary re-fetches
  const queryParams = React.useMemo(() => {
    if (!parcel) return null;

    const params = {
      parcelCode: parcel.parcelCode,
    };

    // Add optional parameters if available
    if (parcel.deliveryDetails?.address?.postcode) {
      params.postcode = parcel.deliveryDetails.address.postcode;
    }

    if (parcel.orderId) {
      params.orderId = parcel.orderId;
    }

    if (parcel.parcelNumber) {
      params.parcelNumber = parcel.parcelNumber;
    }

    return params;
  }, [
    parcel?.parcelCode,
    parcel?.deliveryDetails?.address?.postcode,
    parcel?.orderId,
    parcel?.parcelNumber,
  ]);

  const fetchChatAvailability = useCallback(
    async (queryParams = {}) => {
      if (!parcel?.parcelNumber) return;

      try {
        setChatConfig(null);
        setChatConfigLoading(true);

        const chatConfigResponse = await fetchChatConfig(queryParams);
        const chatConfig = {
          ...CHAT_CONFIG,
          ...chatConfigResponse,
          parcelNumber: parcel?.parcelNumber,
          parcelCode: parcel?.parcelCode,
          postcode: parcel?.deliveryDetails?.address?.postcode,
          orderId: parcel?.orderId,
        };

        setChatConfig(chatConfig);

        dispatch(
          requestChatDetails(
            parcel?.parcelNumber,
            parcel?.parcelCode,
            parcel?.orderId,
            parcel?.deliveryDetails?.address?.postcode,
            null,
            null,
            null // Collections disable bot, parcels let API decide
          )
        );
      } catch (error) {
        setChatConfig(null);
      } finally {
        setChatConfigLoading(false);
      }
    },
    [dispatch, parcel]
  );

  const loadChatAvailability = React.useCallback(
    () => fetchChatAvailability(queryParams),
    [fetchChatAvailability, queryParams]
  );

  // Fetch chat configuration to determine availability
  React.useEffect(() => {
    fetchChatAvailability();
  }, [fetchChatAvailability]);

  const showChatIcon = React.useMemo(
    () =>
      chatOrigin &&
      !isKMIinActions &&
      delay10s &&
      isChatAvailable &&
      !chatConfigLoading,
    [chatOrigin, isKMIinActions, delay10s, isChatAvailable, chatConfigLoading]
  );

  const { deliveredToConsumer, nextSteps, parcelStatusType } = React.useMemo(
    () => ({
      deliveredToConsumer: parcel?.deliveredToConsumer,
      nextSteps: parcel?.nextSteps,
      parcelStatusType: parcel?.parcelStatusType,
    }),
    [parcel?.deliveredToConsumer, parcel?.nextSteps, parcel?.parcelStatusType]
  );

  useChatIconWithAction({
    showChat,
    setShowChat,
    setLoadChat,
    loadChat,
    showChatIcon,
    chatOrigin,
    loadFn: loadChatAvailability,
  });

  const styles = useStyles();
  const preferences = React.useMemo(
    () => getParcelRoadMapPreferences(parcel, availableActions),
    [parcel, availableActions]
  );

  const noWHNStatuses = React.useMemo(
    () => [
      ENUM.PARCEL_STATUS.COLLECT_FROM_DEPOT_INSTRUCTIONS,
      ENUM.PARCEL_STATUS.PICKUP_DELIVERY,
      ENUM.PARCEL_STATUS.PICKUP_DELIVERY_COLLECTED,
    ],
    []
  );

  const noNextSteps = React.useMemo(
    () =>
      get(nextSteps, 'code') === 'N' ||
      get(nextSteps, 'code') === null ||
      (deliveredToConsumer === false &&
        noWHNStatuses.includes(parcelStatusType)),
    [nextSteps, deliveredToConsumer, parcelStatusType, noWHNStatuses]
  );

  return (
    <>
      {noNextSteps ? (
        <NoNexSteps parcel={parcel} />
      ) : (
        <BackToTrackingMapContainer onClick={goToTracking}>
          <Container
            maxWidth="md"
            className={styles.container}
            classes={{ maxWidthMd: styles.width }}
          >
            <TrackingStatus
              status={parcel.parcelStatusHtml}
              className={styles.status}
            />
            <Typography className={styles.title}>
              {WHAT_HAPPENS_NEXT_HEADER}
            </Typography>
            <div className={styles.contentWrapper}>
              <div className={styles.stepsWrapper}>
                <NextSteps
                  preferences={preferences}
                  parcel={parcel}
                  availableActions={availableActions}
                />
              </div>
              <div className={styles.divider} />
              <div className={styles.changeWrapper}>
                <Button type="submit" onClick={onChangeDelivery}>
                  {CHANGE_DELIVERY}
                </Button>
              </div>
            </div>
          </Container>
        </BackToTrackingMapContainer>
      )}
      <ChatBotWrapper
        show={showChat}
        loadChat={loadChat}
        parcel={parcel}
        chatConfig={chatConfig}
        chatAvailable={isChatAvailable}
        setShowChat={setShowChat}
        setLoadChat={setLoadChat}
        onClose={() => fetchChatAvailability()}
      />
    </>
  );
};

const createSpeedDealControlActions = props => [
  trackAnotherParcelSpeedDeal(props),
  goToParcelInfoSpeedDeal(props),
  goBackToDeliveryDetailsSpeedDeal(props),
];

const mapStateToProps = (state, { match }) => {
  const parcelId = match.params.parcelId;

  return {
    deliveryActions: getDeliveryActionsByDepotCode(state, parcelId),
    claims: getClaimsData(state, parcelId),
  };
};

const createHeaderControlActions = props => {
  const generalHeaderControlActions = [
    trackAnotherParcelHeader(props),
    goToParcelInfoHeader(props),
    backToDpdSiteHeader(props),
    backToGreenSiteHeader(props),
    getDpdAppHeader(props),
  ];

  return generalHeaderControlActions;
};

const WhatHappensNext = compose(
  withRouter,
  withParcel(),
  withFetch(({ parcel }) => fetchActionsNeighboursAndDriver({ parcel })),
  connect(mapStateToProps),
  withState('showChat', 'setShowChat', false),
  withState('loadChat', 'setLoadChat', false),
  withRemoteConfig(),
  withProps(
    ({ history, parcel, claims: { origin }, fetchedData: { neighbours } }) => ({
      goToTracking: () => goToTracking({ history, parcel }),
      neighbours,
      chatOrigin:
        origin === ORIGIN.EMAIL ||
        origin === ORIGIN.SMS ||
        origin === ORIGIN.PRCU,
      onChangeDelivery: () => {
        goToDeliveryOptions({
          parcel,
          history,
        });
      },
    })
  ),
  withTrackProps({
    setShowChat: open =>
      createLogEntryPayload(SPEED_DEAL.CLICK_CHAT_ICON, { open }),
    goToTracking: WHAT_HAPPENS_NEXT.GO_TO_TRACKING_MAP,
    onChangeDelivery: WHAT_HAPPENS_NEXT.ON_CHANGE_THIS_DELIVERY,
  }),
  withModal,
  withPageControl({
    title: WHAT_HAPPENS_NEXT_PAGE_HEADER,
    backgroundImage: getBackgroundImage({
      desktopImage: IMAGES.DRIVER_BG,
      mobileImage: IMAGES.DRIVER_BG_MOBILE,
    }),
    backgroundOpacity: 0.87,
    trackable: trackable(WHAT_HAPPENS_NEXT),
    speedDealActions: createSpeedDealControlActions,
    headerMenuActions: createHeaderControlActions,
  })
)(WhatHappensNextComponent);

export default WhatHappensNext;
